import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { push } from 'redux-first-history';
import { toNumber } from 'lodash-es';
import { Link } from '@material-ui/core';

import { UsersIcon } from '@dev/ee-components';
import { safelyEncodeBase64Url } from '@dev/ee-tools';

import { SaveOnboardingStart, getOnboardingStore } from '@/store/onboarding/actions';
import { useCurrentPlanStatus } from '@/services/hooks/useCurrentPlanStatus';
import { ContactsPaths, FormsPaths, LandingPagePaths } from '@/services/dictionaries/Paths';
import { links } from '@/services/dictionaries/links';
import { ALL_CONTACTS_SEGMENT, YT_CREATE_FORMS } from '@/services/constants';

import { OnboardingVideoThumbnail } from '@/components/Onboarding/components/OnboardingVideoThumbnail';
import { OnboardingTask, OnboardingSteps, OnboardingTaskProps } from '../../../types';
import SvgContactGrow from './svgs/svg-contact-grow.svg';
import SvgPublish from './svgs/svg-publish.svg';
import SvgCheckContactGrowth from './svgs/svg-check-contact-grow.svg';
import SvgCreateLandingPage from './svgs/svg-create-landing-page.svg';

const taskIdForm = 'createFormTask';
const taskIdLanding = 'createLandingTask';

export enum GrowAudienceTaskSavedDataKeysEnum {
    CreateFormActive = 'CreateFormActive',
    CreateFormSkipStep1 = 'CreateFormSkipStep1',
    CreateFormSkipStep2 = 'CreateFormSkipStep2',
    CreateFormSkipStep3 = 'CreateFormSkipStep3',
    CreateFormShared = 'CreateFormShared',
    CreateFormContactsChecked = 'CreateFormContactsChecked',

    CreateLandingActive = 'CreateLandingActive',
    CreateLandingSkipStep1 = 'CreateLandingSkipStep1',
    CreateLandingSkipStep2 = 'CreateLandingSkipStep2',
    CreateLandingSkipStep3 = 'CreateLandingSkipStep3',
    CreateLandingSkipStep4 = 'CreateLandingSkipStep4',
    CreateLandingShared = 'CreateLandingShared',
    CreateLandingContactsChecked = 'CreateLandingContactsChecked',
}

export const useGrowAudienceTask = ({ onboardingGoals }: OnboardingTaskProps): OnboardingTask => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { savedData, data } = useSelector(getOnboardingStore);
    const { isPilotPlan } = useCurrentPlanStatus();

    const activeStepsIndex = toNumber(!!savedData?.CreateLandingActive);

    const saveFormAsActive = (): void => {
        dispatch(SaveOnboardingStart({ CreateFormActive: true, CreateLandingActive: false }));
    };

    const saveLandingAsActive = (): void => {
        dispatch(SaveOnboardingStart({ CreateFormActive: false, CreateLandingActive: true }));
    };

    const openAllContactsList = (isFormList: boolean): void => {
        if (!isFormList) {
            dispatch(SaveOnboardingStart({ CreateLandingContactsChecked: true }));
        } else {
            dispatch(SaveOnboardingStart({ CreateFormContactsChecked: true }));
        }

        dispatch(
            push(ContactsPaths.SingleSegment.replace(':segmentname', safelyEncodeBase64Url(ALL_CONTACTS_SEGMENT)))
        );
    };

    const handleOpenContactsListFromForms = () => {
        openAllContactsList(true);
    };

    const handleOpenContactsListFromLandings = () => {
        openAllContactsList(false);
    };

    const handleGoToCreateForm = () => {
        dispatch(push(FormsPaths.FormCreator));
    };

    const handleShareFormDone = () => {
        dispatch(SaveOnboardingStart({ CreateFormShared: true }));
    };

    const handleGoToLandingPagesGallery = () => {
        dispatch(push(LandingPagePaths.LandingPageCreator));
    };

    const handleShareLandingDone = () => {
        dispatch(SaveOnboardingStart({ CreateLandingShared: true }));
    };

    const createFormSteps: OnboardingSteps = {
        id: taskIdForm,
        title: t('createformtask_wanttocreateform'),
        saveAsActive: saveFormAsActive,
        items: [
            {
                title: t('createformtask_gathercontacts_header'),
                features: [t('createformtask_gathercontacts_feature1'), t('createformtask_gathercontacts_feature2')],
                secondaryContent: <OnboardingVideoThumbnail videoId={YT_CREATE_FORMS} />,
                primaryActionTitle: t('createform'),
                primaryActionCallback: handleGoToCreateForm,
                isSkipped: !!savedData?.CreateFormSkipStep1,
                skipSavedDataKey: GrowAudienceTaskSavedDataKeysEnum.CreateFormSkipStep1,
                isCompleted: data?.OneForm,
            },
            {
                title: t('createformtask_capturenewcontacts_header'),
                features: [
                    t('createformtask_capturenewcontacts_feature1'),
                    t('createformtask_capturenewcontacts_feature2'),
                ],
                secondaryContent: <SvgPublish />,
                primaryActionTitle: t('markasdone'),
                primaryActionCallback: handleShareFormDone,
                isNotReady: !!savedData?.CreateFormSkipStep1 || !data?.OneForm,
                isSkipped: !!savedData?.CreateFormSkipStep2,
                skipSavedDataKey: GrowAudienceTaskSavedDataKeysEnum.CreateFormSkipStep2,
                isCompleted: !!savedData?.CreateFormShared,
            },
            {
                title: t('createformtask_checkcontactgrowth_header'),
                features: [
                    t('createformtask_checkcontactgrowth_feature1'),
                    <Trans
                        key="createformtask_checkcontactgrowth_feature2"
                        i18nKey="createformtask_checkcontactgrowth_feature2"
                        components={{ url: <Link href={links.contactStatuses} target="_blank" /> }}
                    />,
                    ...(!isPilotPlan ? [t('createformtask_checkcontactgrowth_feature3')] : []),
                ],
                secondaryContent: <SvgCheckContactGrowth />,
                primaryActionTitle: t('checkcontacts'),
                primaryActionCallback: handleOpenContactsListFromForms,
                isNotReady:
                    !!savedData?.CreateFormSkipStep1 ||
                    !!savedData?.CreateFormSkipStep2 ||
                    !data?.OneForm ||
                    !savedData?.CreateFormShared,
                isSkipped: !!savedData?.CreateFormSkipStep3,
                skipSavedDataKey: GrowAudienceTaskSavedDataKeysEnum.CreateFormSkipStep3,
                isCompleted: !!savedData?.CreateFormContactsChecked,
            },
        ],
    };

    const createLandingSteps: OnboardingSteps = {
        id: taskIdLanding,
        title: t('createlandingtask_wanttocreatelanding'),
        saveAsActive: saveLandingAsActive,
        items: [
            // {
            //     title: t('createlandingtask_createform_header'),
            //     features: [t('createlandingtask_createform_feature1'), t('createlandingtask_createform_feature2')],
            //     secondaryContent: <SvgContactGrow />,
            //     primaryActionTitle: t('createform'),
            //     primaryActionCallback: handleGoToCreateForm,
            //     isSkipped: !!savedData?.CreateLandingSkipStep1,
            //     skipSavedDataKey: GrowAudienceTaskSavedDataKeysEnum.CreateLandingSkipStep1,
            //     isCompleted: data?.OneForm,
            // },
            {
                title: t('createlandingtask_designlanding_header'),
                features: [
                    t('createlandingtask_designlanding_feature1'),
                    t('createlandingtask_designlanding_feature2'),
                   t('createlandingtask_designlanding_feature3')
                ],
                secondaryContent: <SvgCreateLandingPage />,
                primaryActionTitle: t('createlanding'),
                primaryActionCallback: handleGoToLandingPagesGallery,
                // isNotReady: !!savedData?.CreateLandingSkipStep1 || !data?.OneForm,
                isSkipped: !!savedData?.CreateLandingSkipStep1,
                skipSavedDataKey: GrowAudienceTaskSavedDataKeysEnum.CreateLandingSkipStep2,
                isCompleted: data?.OneLandingPage,
            },
            {
                title: t('createlandingtask_capturenewcontacts_header'),
                features: [
                    t('createlandingtask_capturenewcontacts_feature1'),
                    t('createlandingtask_capturenewcontacts_feature2'),
                ],
                secondaryContent: <SvgPublish />,
                primaryActionTitle: t('markasdone'),
                primaryActionCallback: handleShareLandingDone,
                isNotReady:
                    // !!savedData?.CreateLandingSkipStep1 ||
                    !data?.OneForm ||
                    !!savedData?.CreateLandingSkipStep1 ||
                    !data?.OneLandingPage,
                isSkipped: !!savedData?.CreateLandingSkipStep2,
                skipSavedDataKey: GrowAudienceTaskSavedDataKeysEnum.CreateLandingSkipStep3,
                isCompleted: !!savedData?.CreateLandingShared,
            },
            {
                title: t('createlandingtask_checkcontactgrowth_header'),
                features: [
                    t('createlandingtask_checkcontactgrowth_feature1'),
                    <Trans
                        key="createlandingtask_checkcontactgrowth_feature2"
                        i18nKey="createlandingtask_checkcontactgrowth_feature2"
                        components={{ url: <Link href={links.contactStatuses} target="_blank" /> }}
                    />,
                    ...(!isPilotPlan ? [t('createlandingtask_checkcontactgrowth_feature3')] : []),
                ],
                secondaryContent: <SvgCheckContactGrowth />,
                primaryActionTitle: t('checkcontacts'),
                primaryActionCallback: handleOpenContactsListFromLandings,
                isNotReady:
                    !!savedData?.CreateLandingSkipStep1 ||
                    !data?.OneForm ||
                    !!savedData?.CreateLandingSkipStep2 ||
                    !data?.OneLandingPage ||
                    // !!savedData?.CreateLandingSkipStep3 ||
                    !savedData?.CreateLandingShared,
                isSkipped: !!savedData?.CreateLandingSkipStep3,
                skipSavedDataKey: GrowAudienceTaskSavedDataKeysEnum.CreateLandingSkipStep4,
                isCompleted: !!savedData?.CreateLandingContactsChecked,
            },
        ],
    };

    return {
        header: t('growyouraudiencetask_header'),
        icon: <UsersIcon />,
        title: t('growyouraudiencetask_title'),
        description: t('growyouraudiencetask_description'),
        steps: [createFormSteps, createLandingSteps],
        activeStepsIndex,
        isVisible: onboardingGoals.isWantExploreAnsweredOnly || onboardingGoals.isGrowingAudienceAnswered,
    };
};
